// features/inventory/pages/StockDetailPage.tsx
/**
 * Stock Detail Page — Single product stock overview with full history.
 *
 * Route: /inventory/levels/:productId
 *
 * Features:
 * - Stock levels across all branches with expandable detail
 * - Transaction history with type filter pills and pagination
 * - Stock valuation breakdown (cost, retail, profit)
 * - Quick actions: adjust stock, view product, confirm
 * - Summary stats: total on hand, available, reserved, sold, purchased
 * - Variance classification if counted vs expected differs
 * - Tabbed interface: Overview | Transactions | Valuation
 * - Responsive design with mobile-friendly layout
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getStockLevels,
    getStockTransactions,
    getStockValuation,
    StockLevel,
    StockTransaction,
} from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getProducts, getProduct, ProductData } from '@/features/products/services/productsApi';
import {
    ArrowLeft,
    Package,
    Store,
    TrendingUp,
    TrendingDown,
    Clock,
    User,
    RefreshCw,
    Layers,
    AlertTriangle,
    CheckCircle,
    ShieldCheck,
    History,
    Calculator,
    RotateCcw,
    Eye,
    ChevronDown,
    ChevronUp,
    ShoppingCart,
    Truck,
    ArrowRightLeft,
    PackageX,
    ClipboardList,
    PackageCheck,
    ChefHat,
    FlaskConical,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type TabType = 'overview' | 'transactions' | 'valuation';

interface TransactionTypeConfig {
    icon: React.ElementType;
    color: string;
    bg: string;
    label: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const TRANSACTION_TYPE_CONFIG: Record<string, TransactionTypeConfig> = {
    purchase: {
        icon: Truck,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        label: 'Purchase',
    },
    sale: {
        icon: ShoppingCart,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        label: 'Sale',
    },
    adjustment: {
        icon: RotateCcw,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        label: 'Adjustment',
    },
    waste: {
        icon: PackageX,
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        label: 'Waste',
    },
    damage: {
        icon: PackageX,
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        label: 'Damage',
    },
    correction: {
        icon: RotateCcw,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        label: 'Correction',
    },
    transfer_in: {
        icon: ArrowRightLeft,
        color: 'text-cyan-600',
        bg: 'bg-cyan-50',
        label: 'Transfer In',
    },
    transfer_out: {
        icon: ArrowRightLeft,
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        label: 'Transfer Out',
    },
    found_in_count: {
        icon: ClipboardList,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        label: 'Found in Count',
    },
    reservation: {
        icon: ShieldCheck,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        label: 'Reservation',
    },
    initial: {
        icon: PackageCheck,
        color: 'text-teal-600',
        bg: 'bg-teal-50',
        label: 'Initial Stock',
    },
    production_consumption: {
        icon: ChefHat,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        label: 'Kitchen Production',
    },
    bom_consumption: {
        icon: FlaskConical,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        label: 'BOM Usage',
    },
    return: {
        icon: RotateCcw,
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        label: 'Return',
    },
};

const TRANSACTION_TYPES = Object.keys(TRANSACTION_TYPE_CONFIG);

const PAGE_SIZE = 20;

// ============================================================
// SUB-COMPONENTS
// ============================================================

function QuickStat({
    label,
    value,
    icon: Icon,
    color,
    warning,
}: {
    label: string;
    value: string | number;
    icon: React.ElementType;
    color: string;
    warning?: boolean;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={clsx(
                'bg-white rounded-xl border p-3 transition-all hover:shadow-md',
                warning ? 'border-amber-200' : 'border-slate-200'
            )}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        {label}
                    </p>
                    <p
                        className={clsx(
                            'text-xl font-bold mt-0.5',
                            warning ? 'text-amber-600' : 'text-slate-900'
                        )}
                    >
                        {value}
                    </p>
                </div>
                <div className={clsx('p-2 rounded-lg bg-gradient-to-br', color)}>
                    <Icon className="w-4 h-4 text-white" />
                </div>
            </div>
        </motion.div>
    );
}

function OverviewTab({
    stockLevels,
    totalSold,
    totalPurchased,
    totalAdjusted,
    getBranchName,
    navigate,
    productId,
}: {
    stockLevels: StockLevel[];
    totalSold: number;
    totalPurchased: number;
    totalAdjusted: number;
    getBranchName: (id: string) => string;
    navigate: ReturnType<typeof useNavigate>;
    productId: string;
}) {
    const [expandedBranch, setExpandedBranch] = useState<string | null>(null);

    const handleToggleExpand = useCallback((branchId: string) => {
        setExpandedBranch((prev) => (prev === branchId ? null : branchId));
    }, []);

    return (
        <div className="space-y-5">
            {/* Transaction Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card>
                    <CardContent className="p-5 text-center">
                        <div className="p-3 rounded-xl bg-blue-100 w-fit mx-auto mb-3">
                            <ShoppingCart className="w-6 h-6 text-blue-600" />
                        </div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">
                            Total Sold
                        </p>
                        <p className="text-3xl font-black text-slate-900 mt-1">
                            {totalSold.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">units</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-5 text-center">
                        <div className="p-3 rounded-xl bg-emerald-100 w-fit mx-auto mb-3">
                            <Truck className="w-6 h-6 text-emerald-600" />
                        </div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">
                            Total Purchased
                        </p>
                        <p className="text-3xl font-black text-slate-900 mt-1">
                            {totalPurchased.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">units</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-5 text-center">
                        <div className="p-3 rounded-xl bg-amber-100 w-fit mx-auto mb-3">
                            <RotateCcw className="w-6 h-6 text-amber-600" />
                        </div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">
                            Total Adjusted
                        </p>
                        <p className="text-3xl font-black text-slate-900 mt-1">
                            {totalAdjusted.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                            units (waste/damage/corrections)
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Stock by Branch */}
            <Card>
                <CardContent className="p-0">
                    <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                <Store className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-800">
                                    Stock by Branch
                                </h3>
                                <p className="text-xs text-slate-500">
                                    {stockLevels.length} branch
                                    {stockLevels.length !== 1 ? 'es' : ''}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {stockLevels.length === 0 ? (
                            <div className="text-center py-12">
                                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                <p className="text-sm text-slate-500">
                                    No stock records for this product
                                </p>
                            </div>
                        ) : (
                            stockLevels.map((level) => {
                                const onHand = parseFloat(
                                    level.quantityOnHand || '0'
                                );
                                const available = parseFloat(
                                    level.availableQuantity || '0'
                                );
                                const reserved = parseFloat(
                                    level.reservedQuantity || '0'
                                );
                                const isOut = available <= 0;
                                const isLow =
                                    !isOut &&
                                    available <= level.lowStockThreshold;
                                const isExpanded =
                                    expandedBranch === level.id;

                                return (
                                    <div key={level.id}>
                                        <div
                                            onClick={() =>
                                                handleToggleExpand(level.id)
                                            }
                                            className={clsx(
                                                'p-5 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors',
                                                isOut && 'bg-rose-50/30',
                                                isLow && 'bg-amber-50/30'
                                            )}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div
                                                    className={clsx(
                                                        'p-2.5 rounded-xl',
                                                        isOut
                                                            ? 'bg-rose-100'
                                                            : isLow
                                                                ? 'bg-amber-100'
                                                                : 'bg-emerald-100'
                                                    )}
                                                >
                                                    <Store
                                                        className={clsx(
                                                            'w-5 h-5',
                                                            isOut
                                                                ? 'text-rose-500'
                                                                : isLow
                                                                    ? 'text-amber-500'
                                                                    : 'text-emerald-500'
                                                        )}
                                                    />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-sm text-slate-800">
                                                        {level.branchName ||
                                                            getBranchName(
                                                                level.branchId
                                                            )}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        {level.confirmationStatus ===
                                                            'confirmed' ? (
                                                            <Badge
                                                                variant="success"
                                                                size="sm"
                                                            >
                                                                ✓ Confirmed
                                                            </Badge>
                                                        ) : level.confirmationStatus ===
                                                            'counted' ? (
                                                            <Badge
                                                                variant="info"
                                                                size="sm"
                                                            >
                                                                Counted
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="warning"
                                                                size="sm"
                                                            >
                                                                Pending
                                                            </Badge>
                                                        )}
                                                        {isOut && (
                                                            <Badge
                                                                variant="danger"
                                                                size="sm"
                                                            >
                                                                Out of Stock
                                                            </Badge>
                                                        )}
                                                        {isLow && (
                                                            <Badge
                                                                variant="warning"
                                                                size="sm"
                                                            >
                                                                Low Stock
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <div className="text-right">
                                                    <p className="text-xs text-slate-400">
                                                        On Hand
                                                    </p>
                                                    <p
                                                        className={clsx(
                                                            'text-lg font-bold',
                                                            isOut
                                                                ? 'text-rose-600'
                                                                : 'text-slate-800'
                                                        )}
                                                    >
                                                        {onHand.toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-slate-400">
                                                        Available
                                                    </p>
                                                    <p
                                                        className={clsx(
                                                            'text-lg font-bold',
                                                            isOut
                                                                ? 'text-rose-600'
                                                                : 'text-slate-800'
                                                        )}
                                                    >
                                                        {available.toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="text-right hidden sm:block">
                                                    <p className="text-xs text-slate-400">
                                                        Reserved
                                                    </p>
                                                    <p className="text-lg font-bold text-slate-600">
                                                        {reserved.toLocaleString()}
                                                    </p>
                                                </div>
                                                {isExpanded ? (
                                                    <ChevronUp className="w-5 h-5 text-slate-400" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                                )}
                                            </div>
                                        </div>

                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{
                                                        height: 0,
                                                        opacity: 0,
                                                    }}
                                                    animate={{
                                                        height: 'auto',
                                                        opacity: 1,
                                                    }}
                                                    exit={{
                                                        height: 0,
                                                        opacity: 0,
                                                    }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-5 pb-5 pt-2 bg-slate-50/50 border-t border-slate-100">
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                            <div className="p-3 bg-white rounded-xl">
                                                                <p className="text-[10px] text-slate-400 uppercase">
                                                                    On Hand
                                                                </p>
                                                                <p className="text-lg font-bold">
                                                                    {onHand.toLocaleString()}
                                                                </p>
                                                            </div>
                                                            <div className="p-3 bg-white rounded-xl">
                                                                <p className="text-[10px] text-slate-400 uppercase">
                                                                    Available
                                                                </p>
                                                                <p className="text-lg font-bold">
                                                                    {available.toLocaleString()}
                                                                </p>
                                                            </div>
                                                            <div className="p-3 bg-white rounded-xl">
                                                                <p className="text-[10px] text-slate-400 uppercase">
                                                                    Reserved
                                                                </p>
                                                                <p className="text-lg font-bold">
                                                                    {reserved.toLocaleString()}
                                                                </p>
                                                            </div>
                                                            <div className="p-3 bg-white rounded-xl">
                                                                <p className="text-[10px] text-slate-400 uppercase">
                                                                    Threshold
                                                                </p>
                                                                <p className="text-lg font-bold">
                                                                    {level.lowStockThreshold}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {level.lastCountedAt && (
                                                            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                Last counted:{' '}
                                                                {new Date(
                                                                    level.lastCountedAt
                                                                ).toLocaleDateString(
                                                                    'en-NG',
                                                                    {
                                                                        day: 'numeric',
                                                                        month: 'short',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                    }
                                                                )}
                                                            </p>
                                                        )}
                                                        <div className="flex gap-2 mt-3">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(
                                                                        `/inventory/adjust?productId=${productId}&branchId=${level.branchId}`
                                                                    );
                                                                }}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                                                                type="button"
                                                            >
                                                                <RotateCcw className="w-3 h-3" />{' '}
                                                                Adjust
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(
                                                                        '/inventory/ledger'
                                                                    );
                                                                }}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                                                type="button"
                                                            >
                                                                <History className="w-3 h-3" />{' '}
                                                                History
                                                            </button>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function TransactionsTab({
    transactions,
    isLoading,
    txnTypeFilter,
    onTxnTypeFilterChange,
    txnPage,
    onTxnPageChange,
    txnTotal,
    txnPages,
}: {
    transactions: StockTransaction[];
    isLoading: boolean;
    txnTypeFilter: string;
    onTxnTypeFilterChange: (type: string) => void;
    txnPage: number;
    onTxnPageChange: (page: number) => void;
    txnTotal: number;
    txnPages: number;
}) {
    const formatNumber = (val: string | undefined): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 3,
            });
    };

    const formatNaira = (val: string | undefined): string => {
        if (!val) return '—';
        const n = parseFloat(val);
        return isNaN(n) ? '—' : `₦${n.toLocaleString()}`;
    };

    return (
        <div className="space-y-4">
            {/* Type Filter Pills */}
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => onTxnTypeFilterChange('')}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all border',
                        !txnTypeFilter
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                    )}
                    type="button"
                >
                    <History className="w-3.5 h-3.5" />
                    All Types
                </button>
                {TRANSACTION_TYPES.map((type) => {
                    const config = TRANSACTION_TYPE_CONFIG[type];
                    if (!config) return null;
                    const Icon = config.icon;
                    const isActive = txnTypeFilter === type;
                    return (
                        <button
                            key={type}
                            onClick={() =>
                                onTxnTypeFilterChange(isActive ? '' : type)
                            }
                            className={clsx(
                                'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all border',
                                isActive
                                    ? `${config.bg} ${config.color} border-current`
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                            )}
                            type="button"
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {config.label}
                        </button>
                    );
                })}
            </div>

            {/* Transactions Table */}
            <Card className="overflow-hidden">
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                ) : transactions.length === 0 ? (
                    <EmptyState
                        icon={<History className="w-12 h-12" />}
                        title="No transactions found"
                        description={
                            txnTypeFilter
                                ? 'No transactions match the selected filter.'
                                : 'No transactions recorded for this product yet.'
                        }
                        size="sm"
                    />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Date
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Quantity
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                            Unit Cost
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">
                                            Branch
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">
                                            By
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden xl:table-cell">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {transactions.map((txn, idx) => {
                                        const config =
                                            TRANSACTION_TYPE_CONFIG[
                                            txn.transactionType
                                            ] || TRANSACTION_TYPE_CONFIG.adjustment;
                                        const Icon = config.icon;
                                        const qty = parseFloat(
                                            txn.quantity || '0'
                                        );
                                        const isPositive = qty >= 0;

                                        return (
                                            <motion.tr
                                                key={txn.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{
                                                    delay: idx * 0.01,
                                                }}
                                                className="hover:bg-slate-50 transition-colors"
                                            >
                                                <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                                                    <p>
                                                        {new Date(
                                                            txn.createdAt
                                                        ).toLocaleDateString(
                                                            'en-NG'
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        {new Date(
                                                            txn.createdAt
                                                        ).toLocaleTimeString(
                                                            'en-NG',
                                                            {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            }
                                                        )}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={clsx(
                                                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                                                            config.bg,
                                                            config.color
                                                        )}
                                                    >
                                                        <Icon className="w-3 h-3" />
                                                        {config.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <span
                                                        className={clsx(
                                                            'text-sm font-bold tabular-nums',
                                                            isPositive
                                                                ? 'text-emerald-600'
                                                                : 'text-rose-600'
                                                        )}
                                                    >
                                                        {isPositive
                                                            ? '+'
                                                            : ''}
                                                        {formatNumber(
                                                            txn.quantity
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right hidden sm:table-cell text-sm text-slate-600">
                                                    {formatNaira(
                                                        txn.unitCost
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 hidden md:table-cell text-sm text-slate-500">
                                                    {txn.branchName || '—'}
                                                </td>
                                                <td className="px-4 py-3 hidden lg:table-cell text-sm text-slate-500">
                                                    {txn.performerName ||
                                                        'System'}
                                                </td>
                                                <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-400 max-w-[200px] truncate">
                                                    {txn.notes || '—'}
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {txnPages > 1 && (
                            <div className="px-6 py-4 bg-slate-50/50 border-t flex items-center justify-between">
                                <p className="text-sm text-slate-500">
                                    Page {txnPage} of {txnPages} ·{' '}
                                    {txnTotal.toLocaleString()} transactions
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() =>
                                            onTxnPageChange(
                                                Math.max(1, txnPage - 1)
                                            )
                                        }
                                        disabled={txnPage === 1}
                                        className="px-4 py-2 border rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100"
                                        type="button"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() =>
                                            onTxnPageChange(
                                                Math.min(
                                                    txnPages,
                                                    txnPage + 1
                                                )
                                            )
                                        }
                                        disabled={txnPage >= txnPages}
                                        className="px-4 py-2 border rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100"
                                        type="button"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}

function ValuationTab({
    valuation,
}: {
    valuation: any[];
}) {
    const formatNumber = (val: string | undefined): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 0,
            });
    };

    const formatNaira = (val: string | undefined): string => {
        if (!val) return '₦0';
        const n = parseFloat(val);
        return isNaN(n) ? '₦0' : `₦${n.toLocaleString()}`;
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                        Branch
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        On Hand
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Cost Price
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Total Value
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Retail Price
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Retail Value
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                        Profit
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {valuation.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="text-center py-12 text-sm text-slate-500"
                                        >
                                            No valuation data available
                                        </td>
                                    </tr>
                                ) : (
                                    valuation.map(
                                        (v: any, idx: number) => (
                                            <tr
                                                key={idx}
                                                className="hover:bg-slate-50"
                                            >
                                                <td className="px-4 py-3 text-sm font-medium">
                                                    {v.branchName ||
                                                        'Unknown'}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm">
                                                    {formatNumber(
                                                        v.quantityOnHand
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm">
                                                    {formatNaira(
                                                        v.costPrice
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm font-bold">
                                                    {formatNaira(
                                                        v.totalValue
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm">
                                                    {formatNaira(
                                                        v.retailPrice
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm font-bold">
                                                    {formatNaira(
                                                        v.totalRetailValue
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600">
                                                    {formatNaira(
                                                        v.potentialProfit
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function StockDetailPage() {
    const navigate = useNavigate();
    const { productId } = useParams<{ productId: string }>();
    const [searchParams] = useSearchParams();
    const user = useAuthStore((s) => s.user);

    // Tab state (supports URL query param ?tab=transactions)
    const initialTab = (searchParams.get('tab') as TabType) || 'overview';
    const [activeTab, setActiveTab] = useState<TabType>(initialTab);

    // Transaction state
    const [txnPage, setTxnPage] = useState(1);
    const [txnTypeFilter, setTxnTypeFilter] = useState('');

    // Handlers
    const handleTxnTypeFilterChange = useCallback((type: string) => {
        setTxnTypeFilter(type);
        setTxnPage(1);
    }, []);

    const handleTxnPageChange = useCallback((page: number) => {
        setTxnPage(page);
    }, []);

    // Queries
    const { data: product } = useQuery({
        queryKey: ['product-detail-stock', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: stockLevels, isLoading: stockLoading } = useQuery({
        queryKey: ['stock-levels-product', user?.tenantId, productId],
        queryFn: async () => {
            const response = await getStockLevels(user!.tenantId, {
                productId,
                limit: 100,
                sortBy: 'quantityOnHand',
                order: 'desc',
            });
            return response?.data || [];
        },
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: txnResponse, isLoading: txnLoading } = useQuery({
        queryKey: [
            'stock-transactions-product',
            user?.tenantId,
            productId,
            txnPage,
            txnTypeFilter,
        ],
        queryFn: () =>
            getStockTransactions(user!.tenantId, {
                productId,
                limit: PAGE_SIZE,
                page: txnPage,
                transactionType: txnTypeFilter || undefined,
            }),
        enabled:
            !!user?.tenantId &&
            !!productId &&
            activeTab === 'transactions',
    });

    const { data: valuation } = useQuery({
        queryKey: ['stock-valuation', user?.tenantId],
        queryFn: () => getStockValuation(user!.tenantId),
        enabled: !!user?.tenantId && activeTab === 'valuation',
    });

    const { data: branches } = useQuery({
        queryKey: ['branches-stock-detail', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
    }>;
    const stockLevelsList = (stockLevels || []) as StockLevel[];
    const transactions = (txnResponse?.data || []) as StockTransaction[];
    const txnTotal = txnResponse?.meta?.totalCount || 0;
    const txnPages = Math.max(1, Math.ceil(txnTotal / PAGE_SIZE));
    const productValuation = (valuation || []).filter(
        (v: any) => v.productId === productId
    );

    // Stats
    const totalOnHand = stockLevelsList.reduce(
        (sum, s) => sum + parseFloat(s.quantityOnHand || '0'),
        0
    );
    const totalAvailable = stockLevelsList.reduce(
        (sum, s) => sum + parseFloat(s.availableQuantity || '0'),
        0
    );
    const totalReserved = stockLevelsList.reduce(
        (sum, s) => sum + parseFloat(s.reservedQuantity || '0'),
        0
    );
    const confirmedBranches = stockLevelsList.filter(
        (s) => s.confirmationStatus === 'confirmed'
    ).length;
    const pendingBranches = stockLevelsList.filter(
        (s) => s.confirmationStatus === 'pending'
    ).length;

    const totalSold = transactions
        .filter((t) => t.transactionType === 'sale')
        .reduce(
            (sum, t) => sum + Math.abs(parseFloat(t.quantity || '0')),
            0
        );
    const totalPurchased = transactions
        .filter((t) => t.transactionType === 'purchase')
        .reduce(
            (sum, t) => sum + parseFloat(t.quantity || '0'),
            0
        );
    const totalAdjusted = transactions
        .filter((t) =>
            [
                'adjustment',
                'correction',
                'waste',
                'damage',
                'found_in_count',
            ].includes(t.transactionType)
        )
        .reduce(
            (sum, t) =>
                sum + Math.abs(parseFloat(t.quantity || '0')),
            0
        );

    const formatNumber = (val: string | number | undefined): string => {
        if (!val) return '0';
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 3,
            });
    };

    const getBranchName = useCallback(
        (id: string) =>
            branchesList.find((b) => b.id === id)?.name || 'Unknown',
        [branchesList]
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory/levels')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <Package className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    {product?.name || 'Product Details'}
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {product?.sku && (
                                        <span className="font-mono mr-2">
                                            {product.sku}
                                        </span>
                                    )}
                                    {stockLevelsList.length} branch
                                    {stockLevelsList.length !== 1
                                        ? 'es'
                                        : ''}{' '}
                                    · {formatNumber(totalOnHand)} on hand
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() =>
                                    navigate(
                                        `/inventory/adjust?productId=${productId}`
                                    )
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/20"
                                type="button"
                            >
                                <RotateCcw className="w-4 h-4" /> Adjust Stock
                            </button>
                            <button
                                onClick={() =>
                                    navigate(
                                        `/inventory/transfers?productId=${productId}`
                                    )
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-purple-200 text-purple-700 rounded-xl hover:bg-purple-50 transition-all font-semibold text-sm shadow-sm"
                                type="button"
                            >
                                <ArrowRightLeft className="w-4 h-4" /> Transfer
                            </button>
                            <button
                                onClick={() =>
                                    navigate(
                                        `/products/${productId}`
                                    )
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Eye className="w-4 h-4" /> View Catalog
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <QuickStat
                        label="On Hand"
                        value={formatNumber(totalOnHand)}
                        icon={Layers}
                        color="from-slate-500 to-slate-700"
                    />
                    <QuickStat
                        label="Available"
                        value={formatNumber(totalAvailable)}
                        icon={CheckCircle}
                        color="from-emerald-500 to-teal-600"
                    />
                    <QuickStat
                        label="Reserved"
                        value={formatNumber(totalReserved)}
                        icon={ShieldCheck}
                        color="from-blue-500 to-indigo-600"
                    />
                    <QuickStat
                        label="Branches"
                        value={stockLevelsList.length}
                        icon={Store}
                        color="from-purple-500 to-indigo-600"
                    />
                    <QuickStat
                        label="Confirmed"
                        value={confirmedBranches}
                        icon={CheckCircle}
                        color="from-emerald-500 to-teal-600"
                    />
                    <QuickStat
                        label="Pending"
                        value={pendingBranches}
                        icon={Clock}
                        color="from-amber-500 to-orange-600"
                        warning={pendingBranches > 0}
                    />
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-0.5 w-fit">
                    {([
                        {
                            key: 'overview' as TabType,
                            label: 'Stock Overview',
                            icon: Layers,
                        },
                        {
                            key: 'transactions' as TabType,
                            label: 'Transactions',
                            icon: History,
                        },
                        {
                            key: 'valuation' as TabType,
                            label: 'Valuation',
                            icon: Calculator,
                        },
                    ] as Array<{
                        key: TabType;
                        label: string;
                        icon: React.ElementType;
                    }>).map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                                activeTab === tab.key
                                    ? 'bg-white text-emerald-600 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                            )}
                            type="button"
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && (
                    <OverviewTab
                        stockLevels={stockLevelsList}
                        totalSold={totalSold}
                        totalPurchased={totalPurchased}
                        totalAdjusted={totalAdjusted}
                        getBranchName={getBranchName}
                        navigate={navigate}
                        productId={productId!}
                    />
                )}

                {activeTab === 'transactions' && (
                    <TransactionsTab
                        transactions={transactions}
                        isLoading={txnLoading}
                        txnTypeFilter={txnTypeFilter}
                        onTxnTypeFilterChange={handleTxnTypeFilterChange}
                        txnPage={txnPage}
                        onTxnPageChange={handleTxnPageChange}
                        txnTotal={txnTotal}
                        txnPages={txnPages}
                    />
                )}

                {activeTab === 'valuation' && (
                    <ValuationTab valuation={productValuation} />
                )}
            </div>
        </div>
    );
}